// ===========================================
// Admin Events Management Page
// ===========================================
// List and manage events with status tabs, search, and stat chips

import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Grid,
  Skeleton,
  Tooltip,
  Alert,
  TablePagination,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  useEvents,
  useEventStats,
  useDeleteEvent,
} from '../../hooks/useEvents';
import { EventListItem, EventStatus } from '../../api/events.api';
import { EVENT_TYPE_LABELS } from '../../constants/eventTypes';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';

// ===========================================
// STATUS CONFIGURATION
// ===========================================

const statusConfig: Record<EventStatus, { label: string; bgcolor: string; color: string }> = {
  DRAFT: { label: 'Draft', bgcolor: '#9DA5AF', color: 'white' },
  PUBLISHED: { label: 'Published', bgcolor: '#4580E4', color: 'white' },
  CANCELLED: { label: 'Cancelled', bgcolor: '#ff4040', color: 'white' },
  COMPLETED: { label: 'Completed', bgcolor: '#96DAC9', color: '#080808' },
  ARCHIVED: { label: 'Archived', bgcolor: '#FFCE00', color: '#080808' },
};

type StatusFilterValue = 'ALL' | EventStatus;

const statusTabs: { value: StatusFilterValue; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ARCHIVED', label: 'Archived' },
];

// ===========================================
// STAT CHIPS
// ===========================================

function EventStats() {
  const { data: stats, isLoading } = useEventStats();

  if (isLoading) {
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[...Array(6)].map((_, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
              <Skeleton variant="text" width="40%" sx={{ mx: 'auto' }} />
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!stats) return null;

  const statItems = [
    { label: 'Total', value: stats.total, color: '#4580E4' },
    { label: 'Draft', value: stats.draft, color: '#9DA5AF' },
    { label: 'Published', value: stats.published, color: '#4580E4' },
    { label: 'Upcoming', value: stats.upcoming, color: '#96DAC9' },
    { label: 'Completed', value: stats.completed, color: '#96DAC9' },
    { label: 'Cancelled', value: stats.cancelled, color: '#ff4040' },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {statItems.map((item) => (
        <Grid item xs={6} sm={4} md={2} key={item.label}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
            <Typography variant="h4" fontWeight="bold" sx={{ color: item.color }}>
              {item.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.label}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function EventsPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();

  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current !== null) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 400);
  };

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useEvents({
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    search: debouncedSearch || undefined,
    page: page + 1,
    limit: rowsPerPage,
    sortBy: 'startDateTime',
    sortOrder: 'desc',
  });

  const deleteMutation = useDeleteEvent();

  const events: EventListItem[] = data?.data ?? [];
  const totalCount = data?.total ?? 0;


  const handleRowClick = (event: EventListItem) => {
    navigate(`/${schoolSlug}/admin/events/${event.id}`);
  };

  const handleEditClick = (e: React.MouseEvent, eventId: string) => {
    e.stopPropagation();
    navigate(`/${schoolSlug}/admin/events/${eventId}/edit`);
  };

  const handleDeleteClick = (e: React.MouseEvent, eventId: string) => {
    e.stopPropagation();
    setDeleteTargetId(eventId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    await deleteMutation.mutateAsync(deleteTargetId);
    setDeleteTargetId(null);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: StatusFilterValue) => {
    setStatusFilter(newValue);
    setPage(0);
  };

  return (
    <Box>
      <PageHeader
        title="Events"
        subtitle="Manage school events, recitals, workshops and concerts"
        actionLabel="Create Event"
        actionIcon={<AddIcon />}
        onAction={() => navigate(`/${schoolSlug}/admin/events/new`)}
      />

      <EventStats />

      {/* Filters */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={statusFilter}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {statusTabs.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>

        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search by title..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} size="small">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error & { response?: { data?: { message?: string } } })?.response?.data
            ?.message || 'Failed to load events. Please try again.'}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Venue</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>RSVPs</TableCell>
                <TableCell>Tickets</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(8)].map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <EventIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography color="text.secondary">
                        {debouncedSearch || statusFilter !== 'ALL'
                          ? 'No events match your filters'
                          : 'No events yet'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => {
                  const cfg = statusConfig[event.status];
                  return (
                    <TableRow
                      key={event.id}
                      hover
                      onClick={() => handleRowClick(event)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {event.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={EVENT_TYPE_LABELS[event.type]}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(event.startDateTime), 'dd MMM yyyy, h:mm a')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {event.venueName ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cfg.label}
                          size="small"
                          sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {event._count?.EventRSVP ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {event._count?.EventTicket ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit event">
                            <IconButton
                              size="small"
                              onClick={(e) => handleEditClick(e, event.id)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(event.status === 'DRAFT' || event.status === 'CANCELLED') && (
                            <Tooltip title="Delete event">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => handleDeleteClick(e, event.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <ConfirmDialog
        open={!!deleteTargetId}
        title="Delete Event"
        message="Are you sure you want to delete this event? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTargetId(null)}
      />
    </Box>
  );
}
